import type { Pack } from '../types'

/**
 * Pack registry.
 *
 * A "pack" is a curated bundle of rules a user enables with one click
 * — the regulatory equivalent of ESLint `extends: "recommended"`.
 * Pack content ships as code (not per-user data), so every pack is
 * reviewable via git blame and a new EMA/TGA/ICH pack lands as a
 * single frontend commit.
 *
 * Every entry in a pack's `ruleIds` MUST resolve to an id in
 * `ALL_RULES` (tag-rules.ts). The prebuild validator
 * (scripts/validate-tag-rules.mjs) enforces this — a typo fails the
 * build, not a user's profile save.
 *
 * Packs count their rules individually toward MAX_RULES_PER_PROFILE
 * (100 post-A2F-796 — raised from 30 once pack grouping replaced the
 * flat list as the navigation surface). A pack-rule that also comes
 * from a tag dedupes by rule.id, so it counts once.
 */

export const PACKS: Record<string, Pack> = {
  // A2F-545: IND Starter Pack — 4 cover-letter validation rules from
  // docs/specs/chrona-rule-executor-spec.md §Cover letter validation
  // example (multi-check). This is the single pack showcased on the
  // new onboarding page at / (A2F-546). Full FDA Module 3 terminology
  // + grammar content is a later Ruxandra-graded / Flavia-committed
  // effort and is explicitly NOT this pack.
  // A2F-690: pack id stays `ind_v0` to avoid migration of saved profiles
  // that already reference it. Display `name` and `description` updated to
  // value-prop framing now that the pack is the canary for the Pro gate.
  ind_v0: {
    id: 'ind_v0',
    name: 'Cover Letter Validation',
    description:
      'FDA-compliant cover letter checks for IND submissions — flag missing regulatory contact, technical contact, submission size, virus-free attestation, and verify rolling-submission phrasing.',
    tier: 'pro',
    // A2F-1013: bumped 1.1.0 → 1.2.0 as an inert content marker (nothing
    // consumes the version at runtime today). Gives the pack-version
    // rehydration follow-up a delta to detect for existing users who need
    // their stored rules refreshed after this contact-extractor fix.
    version: '1.2.0',
    ruleIds: [
      'ind_cover_letter_regulatory_contact',
      'ind_cover_letter_technical_contact',
      'ind_cover_letter_submission_size',
      'ind_cover_letter_virus_free',
      'ind_cover_letter_rolling_phrasing',
    ],
  },

  // A2F-801: eCTD Document Formatting — body font + image + submission
  // cleanliness checks drawn from eCTD Backbone v3.2.2 Appendix 7 (PDF
  // Specification) plus general regulatory cleanliness. Seven rules
  // ship in v0; six additional rules (black-body text, paragraph
  // shading, section margins, page size, hyperlink target validation,
  // header/footer page numbering) are tracked in A2F-801-followup,
  // gated on executor capability work (run-level iteration, sections
  // target via OOXML, WordApi 1.6+ for paragraph.shading).
  //
  // Pack id stays `ectd_formatting_v0` to lock it against rename once
  // it lands in saved profiles. Same rationale as ind_v0 above —
  // migrating a pack id out of style_profiles JSONB blobs is painful
  // and unrewarded. If the rule set grows substantially in a future
  // bump, ship as `ectd_formatting_v1` rather than rewriting v0 in
  // place; profile.packs entries are pack-version pinned by id.
  ectd_formatting_v0: {
    id: 'ectd_formatting_v0',
    name: 'eCTD Document Formatting',
    description:
      'Body font (Times New Roman 9-12pt), image alt text and size, no strikethrough or hidden text — eCTD Backbone v3.2.2 Appendix 7 plus submission cleanliness.',
    tier: 'pro',
    version: '1.0.0',
    ruleIds: [
      'ectd_body_font_times_new_roman',
      'ectd_body_font_size_min',
      'ectd_body_font_size_max',
      'ectd_image_alt_text_required',
      'ectd_image_width_within_page',
      'ectd_no_strikethrough_text',
      'ectd_no_hidden_text',
    ],
  },

  // A2F-796: FDA Writing Standards — terminology + regulatory grammar
  // rules a writer expects to find under one heading. The pack name is
  // intentionally broader than "FDA Terminology" (the issue's working
  // title) because two of the four rules are regulatory grammar, not
  // pure terminology — "data are" / "criteria are" are FDA-style
  // conventions reviewers actively flag in submissions. ICH M4
  // terminology is here too because eCTD modules are ICH-defined and
  // FDA submissions use them. Zero new executor code — every rule id
  // already lives in tag-rules.ts; this entry just bundles them.
  fda_writing_standards_v0: {
    id: 'fda_writing_standards_v0',
    name: 'FDA Writing Standards',
    description:
      'FDA-preferred terminology and regulatory-writing conventions — drug substance vs. active ingredient, CTD-aligned ICH terms, and the "data are" / "criteria are" plurality conventions reviewers flag.',
    tier: 'pro',
    version: '1.0.0',
    ruleIds: [
      'search_terminology_fda',
      'search_ich_terms',
      'search_grammar_data',
      'search_grammar_criteria',
    ],
  },

  // A2F-803: NDA Reviewers Guide — document-level checks against the
  // FDA Reviewer's Guide structure. v0 ships ONE rule (no inline
  // backbone TOC) consuming the new `document` executor target. Four
  // additional rules (outline format, hyperlink count, PED table,
  // not-mostly-narrative) are deferred until their heuristics are
  // firmer — see A2F-803 plan-round and the follow-up content commits
  // expected post-validation.
  //
  // Pack id locked at `nda_reviewers_guide_v0`. Same rename-prohibited
  // rationale as ind_v0 / ectd_formatting_v0 — migrating a pack id out
  // of style_profiles JSONB blobs is painful and unrewarded. If the
  // rule set grows substantially in a future iteration, ship as
  // `nda_reviewers_guide_v1` rather than rewriting v0 in place.
  nda_reviewers_guide_v0: {
    id: 'nda_reviewers_guide_v0',
    name: 'NDA Reviewers Guide',
    description:
      "Document-level checks for an FDA NDA Reviewer's Guide — flag a pasted-as-text backbone TOC carried over from early drafts.",
    tier: 'pro',
    version: '1.0.0',
    ruleIds: [
      'nda_reviewers_guide_no_backbone_toc',
    ],
  },
}

// A2F-850 followup: packs hidden from users without removing their
// definition from the registry. The pack file content stays untouched
// so re-enabling is a one-line removal from this set rather than a
// commit revert. Validators still iterate PACKS directly, so coverage
// is preserved.
//
// HIDDEN_RULE_IDS is the derived set of rule ids contributed by hidden
// packs — exported alongside so renderers can also suppress the rules
// from Custom-rules sections (catches the case where a user had the
// pack enabled before it was hidden and the pack's rule id is still
// sitting in their profile.rules).
const HIDDEN_PACK_IDS: ReadonlySet<string> = new Set([
  'nda_reviewers_guide_v0',
])

export const ALL_PACKS: Pack[] = Object.values(PACKS).filter(
  (p) => !HIDDEN_PACK_IDS.has(p.id),
)

export const HIDDEN_RULE_IDS: ReadonlySet<string> = new Set(
  Object.values(PACKS)
    .filter((p) => HIDDEN_PACK_IDS.has(p.id))
    .flatMap((p) => p.ruleIds),
)
